import type { ReactNode } from 'react';
import { TouchLock } from '../index';

import type { TimelineFocusSectionInput } from './loggerContext';
import type { useTimelineLogger } from './useTimelineLogger';
import type { useInWorkoutTargets } from './useInWorkoutTargets';
import type { useEffortCommit } from './useEffortCommit';
import type { usePlanCues } from './usePlanCues';
import { FocusPanel } from './FocusPanel';
import { FocusPanelTargetStrip } from './FocusPanelTargetStrip';
import { EmptyFocusCard } from './EmptyFocusCard';
import { RosterStrip } from './RosterStrip';
import { buildTimerStartHandlers } from './timerStartHandlers';

type TimelineLogger = ReturnType<typeof useTimelineLogger>;

// The top of the timeline logger: the roster strip and the FocusPanel — i.e.
// the "pick an exercise" + "the single data-entry point" regions of the three
// the screen doc-comment describes. Split out of TimelineLoggerScreen when the
// shell sat at exactly 500 lines with zero margin (CN-27), following the mould
// TimelineFeedSection already set for the bottom region: a pure render shell
// that reads the same context bag the screen does, owns no state, and receives
// the already-derived values (targets, effort bag, plan cues) as data.
//
// ⚠️ `restFrozen` and `restFreezesNavigation` are two DIFFERENT booleans and
// both arrive here: the first freezes the editing surface (FocusPanel), the
// second the navigation surfaces (the roster's TouchLock) and is false on a
// last-set rest (TM-12). They are passed in — never re-derived from `rest` —
// so the screen keeps computing each exactly once.
export function TimelineFocusSection({
  ctx,
  tl,
  targetRows,
  effort,
  activeCues,
  allLogged,
  restFrozen,
  prescribeAxis,
  prescribeApplyAll,
  onToggleTimed,
  timedActive,
  noteActive,
  prescribeNoteEditor,
}: {
  ctx: TimelineFocusSectionInput;
  tl: TimelineLogger;
  targetRows: ReturnType<typeof useInWorkoutTargets>;
  effort: ReturnType<typeof useEffortCommit>;
  /** The active entry's coach cues, already looked up out of usePlanCues. */
  activeCues: ReturnType<ReturnType<typeof usePlanCues>['get']>;
  /** Every exercise fully logged — drives the empty-slot placeholder copy. */
  allLogged: boolean;
  /** A rest is counting → freeze the EDITING surface. Not the same boolean as
   *  tl.restFreezesNavigation. */
  restFrozen: boolean;
  /** PE-22 K3.2 — prescribe-only slot, same shape as `targetStrip`: the host
   *  (usePrescribeMount/PrescribeTree) builds the element or omits it; this
   *  shell never branches on mode to decide whether to show it. Undefined in
   *  'log'/'log-past'. */
  prescribeAxis?: ReactNode;
  /** PE-22 K3.3 — "Apply to all sets", below the band. Same generic-slot
   *  shape and gating as `prescribeAxis`. Undefined in 'log'/'log-past'. */
  prescribeApplyAll?: ReactNode;
  /** PE-22 K3.4 — "A tempo" chip data: undefined ⇒ FocusPanelChipRow hides
   *  the chip (presence-is-visibility, same convention as the two slots
   *  above). */
  onToggleTimed?: () => void;
  timedActive?: boolean;
  /** PE-22 K3.4 — "Nota" chip's data-driven selected state (has the row a
   *  note?) plus its editor slot, same generic-slot shape as `prescribeAxis`.
   *  The chip's OPEN/CLOSE is local FocusPanel UI state (like Burn+'s bar),
   *  not threaded through here — FocusPanel hands its own close handler to
   *  the render function so the editor's explicit close button can reuse it. */
  noteActive?: boolean;
  prescribeNoteEditor?: (onClose: () => void) => ReactNode;
}) {
  const {
    draft,
    dispatch,
    theme,
    t,
    i18n,
    getExerciseNameBySlug,
    groupLabelFor,
    defaultUnit,
    setPickerOpen,
    setEffortScaleOpen,
    setTipEntryIdx,
    setSwapEntryIdx,
    setGroupPickerEntryIdx,
    setSupersetMenu,
    plate,
    hold,
    timerDefaults,
    mode,
  } = ctx;

  const {
    resolvedActiveId,
    activeEntry,
    activeEntryIdx,
    boundSet,
    boundSetIdx,
    activeExercise,
    canRemove,
    effortScale,
    groupMembers,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
    focusRow,
    confirmSplitGroup,
    onRemoveExercise,
    onChangeSet,
    onAddSet,
    onRemoveSet,
    onToggleLoadedSides,
    cueKey,
    rest,
    restFreezesNavigation,
    flashCue,
  } = tl;

  return draft.entries.length === 0 ? (
    // Blank ad-hoc workout: empty card explains how to begin (Option B).
    // PE-24 — prescribe authoring reuses this same empty slot (no slots on
    // this day yet), but it is AUTHORING, not logging, and its ＋ is gated
    // off below (hideAdd on the roster strip) — so it gets its own copy
    // that neither says "logging" nor invites a tap the card can't honour.
    <EmptyFocusCard
      message={
        mode === 'prescribe'
          ? t('composer.prescribeDayEmpty', {
              defaultValue: 'No exercises on this day yet.',
            })
          : t('mobile.logger.empty')
      }
      addAction={
        mode === 'prescribe'
          ? undefined
          : {
              label: t('mobile.logger.addExercise'),
              onPress: () => setPickerOpen(true),
            }
      }
      theme={theme}
    />
  ) : (
    <>
      {/* Roster hybrid (slice 4) — switch which exercise FocusPanel
          targets; each chip shows state + logged/planned count + group
          marker so the queue reads at a glance (v2.67/v2.68 smoke).
          Auto-expanded at start, collapses on pick (slice C). */}
      <TouchLock locked={restFreezesNavigation}>
        <RosterStrip
          entries={draft.entries}
          activeEntryId={resolvedActiveId}
          onFocus={focusRow}
          resolveName={getExerciseNameBySlug}
          groupLabelFor={groupLabelFor}
          expanded={rosterExpanded}
          onToggleExpanded={() => setRosterExpanded((v) => !v)}
          onAddExercise={() => setPickerOpen(true)}
          hideAdd={mode === 'prescribe'}
          t={t}
          theme={theme}
        />
      </TouchLock>

      {/* The group line is now the FIRST row INSIDE the FocusPanel card
          (Antenore 2026-06-01: "raggruppa assieme la card superset e la
          card esercizio"), so it's no longer rendered separately here. */}
      {activeEntry ? (
        <FocusPanel
          title={getExerciseNameBySlug(
            activeEntry.exerciseId,
            activeEntry.exerciseName,
          )}
          entry={activeEntry}
          set={boundSet}
          activeSetIdx={boundSetIdx >= 0 ? boundSetIdx : null}
          planPerSetNotes={activeCues?.perSetNotes}
          planEntryNote={activeCues?.instruction}
          canRemove={canRemove}
          exercise={activeExercise}
          defaultUnit={defaultUnit}
          effortScale={effortScale}
          onChange={onChangeSet}
          onSelectSet={setSelectedSetIdx}
          onAddSet={onAddSet}
          onRemoveSet={onRemoveSet}
          onToggleUnit={() =>
            dispatch({
              type: 'toggle_unit',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
            })
          }
          onToggleLoadedSides={onToggleLoadedSides}
          onToggleAmrap={() => {
            if (boundSetIdx < 0 || !boundSet) return;
            dispatch({
              type: 'update_set',
              entryIdx: activeEntryIdx,
              setIdx: boundSetIdx,
              // OUTCOME only. `amrapPrescribed` is not in the
              // update_set patch key union at all, so no UI path can
              // rewrite the plan's request from here.
              patch: { amrap: !boundSet.amrap },
            });
          }}
          effortMeter={{
            value: effort.value,
            failed: effort.failed,
            onSelect: effort.onSelect,
            onFail: effort.onFail,
            flashRir: effort.flashRir,
            flashKey: effort.flashKey,
          }}
          // Start-timer affordance for any duration-logged entry —
          // cardio + timed holds (TM-5: cardio had none). Gating +
          // countdown-seed rule live in buildTimerStartHandlers.
          timer={buildTimerStartHandlers({
            entry: activeEntry,
            entryIdx: activeEntryIdx,
            boundSet,
            boundSetIdx,
            hold,
            onOpenDefaults: timerDefaults.open,
          })}
          // Edit-scope (slice 5): on a PAST-SESSION EDIT the structural
          // controls (＋ add set, create/split superset, add chain step)
          // are greyed + balloon-locked; allowed edits (values, remove,
          // fail-toggle, replace/remove exercise) stay live. This is the
          // single place the mode becomes `editLocked` — the ~30 reads
          // downstream are prop-drilling, not re-derivations.
          // ⚠️ PE-22 K0 — this asks 'log-past' EXACTLY, and the widening to
          // `mode !== 'log'` it replaces was the bug waiting for the third
          // mode: in PRESCRIPTION those same controls are not edits to
          // forbid, they ARE the authoring. Locking them would have made the
          // new editor unable to add a set. For 'log'/'log-past' nothing
          // changes.
          editLocked={mode === 'log-past'}
          // PE-22 K3.1 — prescribe authoring only: Fail and Burn+ are silent
          // no-ops there (no outcome to fail, no dropset/myorep chain to
          // run), so the card hides both rather than showing a control that
          // does nothing. 'log'/'log-past' are untouched.
          hideFail={mode === 'prescribe'}
          hideBurnPlus={mode === 'prescribe'}
          hideInert={mode === 'prescribe'}
          prescribeAxis={prescribeAxis}
          prescribeApplyAll={prescribeApplyAll}
          onToggleTimed={onToggleTimed}
          timedActive={timedActive}
          noteActive={noteActive}
          prescribeNoteEditor={prescribeNoteEditor}
          onOpenPlates={() => plate.openForSet(activeEntryIdx, boundSetIdx)}
          onOpenEffortScale={() => setEffortScaleOpen(true)}
          onSwap={() => setSwapEntryIdx(activeEntryIdx)}
          onSplitGroup={
            activeEntry.group
              ? () => confirmSplitGroup(activeEntry.group!.id)
              : undefined
          }
          historyOpen={activeEntry.historyOpen}
          onToggleHistory={() =>
            dispatch({
              type: 'toggle_entry_history',
              entryIdx: activeEntryIdx,
              next: !activeEntry.historyOpen,
            })
          }
          onOpenTip={() => setTipEntryIdx(activeEntryIdx)}
          onRemoveExercise={onRemoveExercise}
          // Group line (first row of the card): standalone → ＋ Superset
          // (opens the picker); grouped → ‹ › A 0/3 … ✕. Create offered
          // only when there's another exercise to pair with.
          groupMembers={groupMembers}
          groupLabel={
            activeEntry.group ? groupLabelFor(activeEntry.group) : ''
          }
          activeEntryId={resolvedActiveId}
          resolveName={getExerciseNameBySlug}
          onSelectMember={focusRow}
          onCreateGroup={
            // ＋ Superset ALWAYS opens the picker on the SUPERSET tab with
            // THIS exercise auto-included (AP-0k2, Antenore: it must go
            // straight to the superset flow — current exercise already in
            // — not the plain Add-exercise flow, even with one exercise;
            // the superset tab's catalog list supplies the partner).
            activeEntry.group === null
              ? () => setGroupPickerEntryIdx(activeEntryIdx)
              : undefined
          }
          cueKey={cueKey}
          rest={rest}
          restFrozen={restFrozen}
          flashCue={flashCue}
          targetStrip={
            <FocusPanelTargetStrip
              rows={targetRows}
              units={defaultUnit}
              locale={i18n.language}
              resolveExerciseName={(slug) => getExerciseNameBySlug(slug, slug)}
              theme={theme}
            />
          }
          onOpenGroupMenu={
            activeEntry.group
              ? () =>
                  setSupersetMenu({
                    groupId: activeEntry.group!.id,
                    entryIdx: activeEntryIdx,
                  })
              : undefined
          }
          confirm={ctx.deps.confirm}
          theme={theme}
        />
      ) : (
        // No exercise focused. Two cases: every set is logged (the
        // workout just completed — point A end-state) → "all logged, add
        // another or save"; otherwise workout start / after a remove →
        // "pick above or add a new one".
        <EmptyFocusCard
          message={
            // PE-24 — in prescribe there is no "logged" end-state and no ＋
            // (hideAdd), so neither the all-logged nor the "add a new one"
            // copy is honest: it points at a control the card does not show.
            mode === 'prescribe'
              ? t('composer.prescribeFocusEmpty', {
                  defaultValue:
                    'Select an exercise above to prescribe its sets.',
                })
              : allLogged
                ? t('mobile.timeline.focusAllLogged', {
                    defaultValue:
                      'All sets logged. Add an exercise above, or save below.',
                  })
                : t('mobile.timeline.focusEmptyHasRoster', {
                    defaultValue:
                      'Select an exercise above to start, or add a new one.',
                  })
          }
          flashOnAppear={mode !== 'prescribe' && allLogged}
          theme={theme}
        />
      )}
    </>
  );
}
